import { useCallback, useState } from "react";
import {
  syncPlayline,
  fetchLatestPlayline,
  type PlaylineEntry,
} from "../../api/playline-persistence";
import { ApiError } from "../../api/client";
import {
  readLocalPlaylineState,
  writeLocalPlaylineState,
} from "./playlineStateStorage";
import type { ConflictPlaylineSnapshot } from "../../components/playline-persistence/ConflictResolutionModal";

/**
 * playline-persistence client 配線: scene 進行ごとに cloud へ progress を sync する hook。
 *
 * 採択 (= 2026-06-02 user 判断): scene 進行ごとに自動 sync (fire-and-forget)。
 *
 * - syncNow(playlineId): local versionVector++ → localStorage 更新 → cloud sync
 *   - 成功: server versionVector で localStorage を再同期
 *   - 409 (= PlaylineVersionConflictError): cloud snapshot を fetch して conflict state set
 * - resolveUseLocal(): force=true で上書き sync、 conflict 解消
 * - resolveUseCloud(): cloud 採用 (= onAdoptCloud callback)、 conflict 解消
 * - dismissConflict(): modal を閉じる (= 「あとで決める」)
 *
 * anonymous (= 401) / network error は silent skip (= 読書ループを巻き込まない)。
 */

export interface PlaylineConflict {
  playlineId: string;
  cloud: ConflictPlaylineSnapshot;
  local: ConflictPlaylineSnapshot;
}

export interface UsePlaylineSyncResult {
  conflict: PlaylineConflict | null;
  syncNow: (playlineId: string) => void;
  resolveUseLocal: () => void;
  resolveUseCloud: (onAdoptCloud: (cloudPlaylineId: string) => void) => void;
  dismissConflict: () => void;
}

function toSnapshot(entry: PlaylineEntry): ConflictPlaylineSnapshot {
  return {
    lastPlayedAt: entry.lastPlayedAt,
    versionVector: entry.versionVector,
    deviceFingerprint: entry.deviceFingerprint,
  };
}

export function usePlaylineSync(baseUrl: string): UsePlaylineSyncResult {
  const [conflict, setConflict] = useState<PlaylineConflict | null>(null);
  // 直近 sync 時の local versionVector を保持 (= force overwrite 時に再利用)
  const [pendingLocalVersion, setPendingLocalVersion] = useState<number>(0);
  const [pendingPlaylineId, setPendingPlaylineId] = useState<string | null>(null);

  const syncNow = useCallback(
    (playlineId: string): void => {
      const prev = readLocalPlaylineState();
      const baseVersion =
        prev !== null && prev.playlineId === playlineId ? prev.versionVector : 0;
      const nextVersion = baseVersion + 1;
      const lastPlayedAt = new Date().toISOString();
      writeLocalPlaylineState({ playlineId, versionVector: nextVersion, lastPlayedAt });
      setPendingLocalVersion(nextVersion);
      setPendingPlaylineId(playlineId);

      // fire-and-forget: 読書ループを blocking しない
      void syncPlayline(baseUrl, { playlineId, lastPlayedAt, versionVector: nextVersion })
        .then((synced) => {
          // server 確定 versionVector で localStorage を再同期
          writeLocalPlaylineState({
            playlineId,
            versionVector: synced.versionVector,
            lastPlayedAt: synced.lastPlayedAt ?? lastPlayedAt,
          });
        })
        .catch(async (err) => {
          // anonymous / network は silent skip
          if (!(err instanceof ApiError) || err.status !== 409) return;
          // 409 conflict: cloud snapshot を fetch して modal trigger
          try {
            const latest = await fetchLatestPlayline(baseUrl);
            if (latest.playline === null) return;
            setConflict({
              playlineId,
              cloud: toSnapshot(latest.playline),
              local: {
                lastPlayedAt,
                versionVector: nextVersion,
                deviceFingerprint: null,
              },
            });
          } catch {
            // snapshot fetch 失敗で modal 出さず silent (= 次 sync で再試行)
          }
        });
    },
    [baseUrl],
  );

  const resolveUseLocal = useCallback((): void => {
    if (pendingPlaylineId === null) {
      setConflict(null);
      return;
    }
    const lastPlayedAt = new Date().toISOString();
    void syncPlayline(baseUrl, {
      playlineId: pendingPlaylineId,
      lastPlayedAt,
      versionVector: pendingLocalVersion,
      force: true,
    })
      .then((synced) => {
        writeLocalPlaylineState({
          playlineId: pendingPlaylineId,
          versionVector: synced.versionVector,
          lastPlayedAt: synced.lastPlayedAt ?? lastPlayedAt,
        });
      })
      .catch(() => {
        // force sync 失敗は silent (= 次 scene 進行で再試行)
      });
    setConflict(null);
  }, [baseUrl, pendingPlaylineId, pendingLocalVersion]);

  const resolveUseCloud = useCallback(
    (onAdoptCloud: (cloudPlaylineId: string) => void): void => {
      const c = conflict;
      setConflict(null);
      if (c !== null) {
        // cloud 採用: local state を cloud version に合わせて上書き + 親に reload 通知
        writeLocalPlaylineState({
          playlineId: c.playlineId,
          versionVector: c.cloud.versionVector,
          lastPlayedAt: c.cloud.lastPlayedAt ?? new Date().toISOString(),
        });
        onAdoptCloud(c.playlineId);
      }
    },
    [conflict],
  );

  const dismissConflict = useCallback((): void => {
    setConflict(null);
  }, []);

  return { conflict, syncNow, resolveUseLocal, resolveUseCloud, dismissConflict };
}
